/**
 * Firebase data layer — comments, favorites, watchlist, continue-watching,
 * view counters, and notification subscription tokens.
 *
 * Each entity is keyed by a stable string (slug for content; uid for users).
 * All hooks are subscription-based; they automatically reflect remote changes.
 *
 * NOTE: all RTDB usage was removed and migrated to Firestore distributed
 * counters so we never hit the 1-write-per-document limit.
 */
import { useEffect, useState } from "react";
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type DocumentData,
} from "firebase/firestore";
import { getDb } from "./firebase";

/* ---------------- Distributed-counter helpers ---------------- */

/** Number of shards for view counters. Increase if you see throttling. */
const VIEW_SHARDS = 10;
/** Number of shards for comment counters. */
const COMMENT_SHARDS = 5;

function bumpCounter(
  db: NonNullable<ReturnType<typeof getDb>>,
  coll: string,
  docId: string,
  shards: number,
): void {
  const shardId = Math.floor(Math.random() * shards);
  const shardRef = doc(db, coll, docId, "shards", String(shardId));
  setDoc(shardRef, { count: increment(1) }, { merge: true }).catch(() => {});
}

function useCounter(coll: string, id: string | undefined | null): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!id) return;
    const db = getDb();
    if (!db) return;
    const q = query(collection(db, coll, id, "shards"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        let total = 0;
        snap.forEach((d) => {
          total += (d.data().count as number) || 0;
        });
        setCount(total);
      },
      () => {
        /* ignore permission / network errors */
      },
    );
    return () => unsub();
  }, [id]);
  return count;
}

/**
 * One-shot (non-realtime) sum of a distributed counter's shards.
 * Used by list/grid cards where a persistent realtime listener per card would
 * be wasteful. Reads once and returns the total; never throws.
 */
async function sumShardsOnce(coll: string, id: string): Promise<number> {
  const db = getDb();
  if (!db || !id) return 0;
  try {
    const snap = await getDocs(collection(db, coll, id, "shards"));
    let total = 0;
    snap.forEach((d) => {
      total += (d.data().count as number) || 0;
    });
    return total;
  } catch {
    return 0;
  }
}

/**
 * Lightweight per-card counts (views + comments) for homepage / grid cards.
 *
 * Performance-conscious by design:
 *   - Uses a single one-time read per counter (getDocs), NOT a realtime
 *     onSnapshot listener, so scrolling a grid of many cards never opens
 *     dozens of long-lived Firestore streams.
 *   - Deferred to browser idle time so it never competes with first paint or
 *     hydration (keeps Lighthouse/PageSpeed scores intact).
 *   - Only runs on the client and only for cards that actually mount (rows are
 *     lazily mounted when scrolled into view).
 *
 * Returns `null` while loading so the caller can render a neutral placeholder.
 */
export function useCardCounts(
  slug: string | undefined | null,
  threadId: string | undefined | null,
): { views: number; comments: number } | null {
  const [counts, setCounts] = useState<{ views: number; comments: number } | null>(null);
  useEffect(() => {
    if (!slug || typeof window === "undefined") return;
    let cancelled = false;
    const run = () => {
      Promise.all([
        sumShardsOnce("counters", slug),
        threadId ? sumShardsOnce("commentCounters", threadId) : Promise.resolve(0),
      ]).then(([views, comments]) => {
        if (!cancelled) setCounts({ views, comments });
      });
    };
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (h: number) => void;
    };
    let idleHandle: number | undefined;
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    if (typeof w.requestIdleCallback === "function") {
      idleHandle = w.requestIdleCallback(run, { timeout: 2000 });
    } else {
      timeoutHandle = setTimeout(run, 300);
    }
    return () => {
      cancelled = true;
      if (idleHandle !== undefined && typeof w.cancelIdleCallback === "function") {
        w.cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
    };
  }, [slug, threadId]);
  return counts;
}

/* ---------------- Types ---------------- */

export type ReactionKind = "likes" | "loves" | "sads" | "angrys" | "bads";

export type Comment = {
  id: string;
  threadId: string;
  threadTitle?: string | null;
  uid: string;
  displayName: string;
  avatar?: string | null;
  text: string;
  createdAt: number;
  editedAt?: number | null;
  hidden?: boolean;
  parentId?: string | null;
  likes?: string[];
  loves?: string[];
  sads?: string[];
  angrys?: string[];
  bads?: string[];
};

export type AppNotification = {
  id: string;
  type: "reply" | "reaction" | "thread";
  actorUid: string;
  actorName: string;
  actorAvatar?: string | null;
  commentId: string;
  parentId?: string | null;
  threadId: string;
  threadTitle?: string | null;
  reactionKind?: ReactionKind | null;
  snippet?: string | null;
  read: boolean;
  createdAt: number;
};

export type FavoriteRef = { slug: string; kind: "movie" | "series"; addedAt: number };
export type WatchlistRef = FavoriteRef;
export type WatchHistoryRef = {
  slug: string;
  kind: "movie" | "episode";
  season?: number;
  episode?: number;
  position: number;
  duration: number;
  percent: number;
  updatedAt: number;
};

/* ---------------- Comments ---------------- */

/** Subscribe to a comment thread. Returns ([], false) during SSR. */
export function useComments(threadId: string): { comments: Comment[]; loading: boolean } {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const db = getDb();
    if (!db || !threadId) {
      setLoading(false);
      return;
    }
    // NOTE: we intentionally do NOT use orderBy("createdAt") in the query.
    // Two reasons:
    //   1. The composite index (threadId + createdAt) may not be deployed,
    //      which would cause onSnapshot to fail silently and the list to
    //      stay empty (the "comments disappear on every load" bug).
    //   2. New comments are written with serverTimestamp() which is null
    //      locally for ~200ms, and orderBy would temporarily exclude them
    //      (the "comment flashes then disappears" bug).
    // We sort client-side after receiving the snapshot instead — cheap for
    // the 200 max threads we cap below.
    const q = query(
      collection(db, "comments"),
      where("threadId", "==", threadId),
      limit(200),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const out: Comment[] = [];
        snap.forEach((s) => {
          const d = s.data() as DocumentData;
          // Skip hidden comments unless the viewer is admin (admins use a
          // separate dashboard query for moderation).
          out.push({
            id: s.id,
            threadId: d.threadId,
            uid: d.uid,
            displayName: d.displayName ?? "User",
            avatar: d.avatar ?? null,
            text: d.text ?? "",
            createdAt: d.createdAt?.toMillis?.() ?? Date.now(),
            editedAt: d.editedAt?.toMillis?.() ?? null,
            threadTitle: d.threadTitle ?? null,
            hidden: !!d.hidden,
            parentId: d.parentId ?? null,
            likes: Array.isArray(d.likes) ? d.likes : [],
            loves: Array.isArray(d.loves) ? d.loves : [],
            sads: Array.isArray(d.sads) ? d.sads : [],
            angrys: Array.isArray(d.angrys) ? d.angrys : [],
            bads: Array.isArray(d.bads) ? d.bads : [],
          });
        });
        out.sort((a, b) => b.createdAt - a.createdAt);
        setComments(out);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return () => unsub();
  }, [threadId]);

  return { comments, loading };
}

export async function postComment(input: {
  threadId: string;
  threadTitle?: string | null;
  uid: string;
  displayName: string;
  avatar?: string | null;
  text: string;
  parentId?: string | null;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  const db = getDb();
  if (!db) return { ok: false, error: "Firebase غير متاح" };
  const text = input.text.trim();
  if (!text) return { ok: false, error: "التعليق فارغ" };
  if (text.length > 2000) return { ok: false, error: "التعليق طويل جداً (الحد 2000 حرف)" };
  try {
    const ref = await addDoc(collection(db, "comments"), {
      threadId: input.threadId,
      threadTitle: input.threadTitle ?? null,
      uid: input.uid,
      displayName: input.displayName,
      avatar: input.avatar ?? null,
      text,
      createdAt: serverTimestamp(),
      hidden: false,
      parentId: input.parentId ?? null,
      likes: [],
      loves: [],
      sads: [],
      angrys: [],
      bads: [],
    });
    bumpCounter(db, "commentCounters", input.threadId, COMMENT_SHARDS);

    // If this is a reply, notify the parent comment's author (best-effort).
    // Also collect parent uid to avoid double-notifying via the thread-watchers
    // fan-out below.
    let parentUid: string | undefined;
    if (input.parentId) {
      try {
        const parentSnap = await getDoc(doc(db, "comments", input.parentId));
        parentUid = parentSnap.data()?.uid as string | undefined;
        if (parentUid && parentUid !== input.uid) {
          await addDoc(collection(db, "users", parentUid, "notifications"), {
            type: "reply",
            actorUid: input.uid,
            actorName: input.displayName,
            actorAvatar: input.avatar ?? null,
            commentId: ref.id,
            parentId: input.parentId,
            threadId: input.threadId,
            threadTitle: input.threadTitle ?? null,
            reactionKind: null,
            snippet: text.slice(0, 160),
            read: false,
            createdAt: serverTimestamp(),
          });
        }
      } catch { /* notifications are best-effort */ }
    }

    // Fan out a "thread" notification to every user who favorited or saved
    // this item to their watchlist. We only fan out on TOP-LEVEL comments —
    // replies are notified individually above. Excludes the comment author
    // and (when applicable) the already-notified parent-comment author.
    try {
      const watchersSnap = await getDoc(doc(db, "threadWatchers", input.threadId));
      if (watchersSnap.exists()) {
        const wd = watchersSnap.data() as DocumentData;
        const favs: string[] = Array.isArray(wd.favoriteUids) ? wd.favoriteUids : [];
        const wls: string[] = Array.isArray(wd.watchlistUids) ? wd.watchlistUids : [];
        const seen = new Set<string>();
        const targets: string[] = [];
        for (const u of [...favs, ...wls]) {
          if (!u || u === input.uid || u === parentUid) continue;
          if (seen.has(u)) continue;
          seen.add(u);
          targets.push(u);
        }
        // Firestore batch cap = 500; each recipient = 1 write. Cap at 400 to be safe.
        const MAX_FANOUT = 400;
        const slice = targets.slice(0, MAX_FANOUT);
        if (slice.length > 0) {
          const batch = writeBatch(db);
          for (const targetUid of slice) {
            const nRef = doc(collection(db, "users", targetUid, "notifications"));
            batch.set(nRef, {
              type: "thread",
              actorUid: input.uid,
              actorName: input.displayName,
              actorAvatar: input.avatar ?? null,
              commentId: ref.id,
              parentId: null,
              threadId: input.threadId,
              threadTitle: input.threadTitle ?? null,
              reactionKind: null,
              snippet: text.slice(0, 160),
              read: false,
              createdAt: serverTimestamp(),
            });
          }
          await batch.commit();
        }
      }
    } catch { /* thread fan-out is best-effort */ }

    return { ok: true };
  } catch {
    return { ok: false, error: "تعذّر إرسال التعليق" };
  }
}

/** Owner-only edit of own comment text. */
export async function editComment(
  commentId: string,
  uid: string,
  text: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const db = getDb();
  if (!db) return { ok: false, error: "Firebase غير متاح" };
  const t = text.trim();
  if (!t) return { ok: false, error: "التعليق فارغ" };
  if (t.length > 2000) return { ok: false, error: "التعليق طويل جداً (الحد 2000 حرف)" };
  try {
    const snap = await getDoc(doc(db, "comments", commentId));
    if (!snap.exists() || snap.data().uid !== uid) {
      return { ok: false, error: "غير مسموح" };
    }
    await updateDoc(doc(db, "comments", commentId), {
      text: t,
      editedAt: serverTimestamp(),
    });
    return { ok: true };
  } catch {
    return { ok: false, error: "تعذّر تعديل التعليق" };
  }
}

/** Toggle a reaction by the given user on a comment.
 *  When turning ON a reaction on someone else's comment, also drop a
 *  lightweight notification in the comment-owner's inbox. */
export async function toggleCommentReaction(
  commentId: string,
  uid: string,
  kind: ReactionKind,
  on: boolean,
  actor?: { displayName: string; avatar?: string | null },
): Promise<void> {
  const db = getDb();
  if (!db || !uid) return;
  await updateDoc(doc(db, "comments", commentId), {
    [kind]: on ? arrayUnion(uid) : arrayRemove(uid),
  });
  if (!on || !actor) return;
  try {
    const snap = await getDoc(doc(db, "comments", commentId));
    const d = snap.data();
    if (!d || d.uid === uid) return;
    await addDoc(collection(db, "users", d.uid, "notifications"), {
      type: "reaction",
      actorUid: uid,
      actorName: actor.displayName,
      actorAvatar: actor.avatar ?? null,
      commentId,
      parentId: d.parentId ?? null,
      threadId: d.threadId,
      threadTitle: d.threadTitle ?? null,
      reactionKind: kind,
      snippet: (d.text as string | undefined)?.slice(0, 160) ?? null,
      read: false,
      createdAt: serverTimestamp(),
    });
  } catch { /* best-effort */ }
}

export async function deleteComment(id: string): Promise<void> {
  const db = getDb();
  if (!db) return;
  await deleteDoc(doc(db, "comments", id));
}

/** Admin: hide instead of delete (preserve audit trail). */
export async function setCommentHidden(id: string, hidden: boolean): Promise<void> {
  const db = getDb();
  if (!db) return;
  await updateDoc(doc(db, "comments", id), { hidden });
}

/* ---------------- Notifications inbox ---------------- */

/** Subscribe to the current user's notification inbox (latest 50). */
export function useNotifications(uid: string | null): {
  items: AppNotification[];
  unread: number;
  loading: boolean;
} {
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!uid) { setItems([]); setLoading(false); return; }
    const db = getDb();
    if (!db) { setLoading(false); return; }
    const q = query(
      collection(db, "users", uid, "notifications"),
      orderBy("createdAt", "desc"),
      limit(50),
    );
    const unsub = onSnapshot(q, (snap) => {
      const out: AppNotification[] = [];
      snap.forEach((s) => {
        const d = s.data() as DocumentData;
        out.push({
          id: s.id,
          type: d.type,
          actorUid: d.actorUid,
          actorName: d.actorName ?? "User",
          actorAvatar: d.actorAvatar ?? null,
          commentId: d.commentId,
          parentId: d.parentId ?? null,
          threadId: d.threadId,
          threadTitle: d.threadTitle ?? null,
          reactionKind: (d.reactionKind ?? null) as ReactionKind | null,
          snippet: d.snippet ?? null,
          read: !!d.read,
          createdAt: d.createdAt?.toMillis?.() ?? Date.now(),
        });
      });
      setItems(out);
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [uid]);
  const unread = items.reduce((n, x) => n + (x.read ? 0 : 1), 0);
  return { items, unread, loading };
}

/** Mark every unread notification of `uid` as read, in one batch. */
export async function markAllNotificationsRead(
  uid: string,
  ids: string[],
): Promise<void> {
  const db = getDb();
  if (!db || !uid || ids.length === 0) return;
  const batch = writeBatch(db);
  for (const id of ids) {
    batch.update(doc(db, "users", uid, "notifications", id), { read: true });
  }
  try { await batch.commit(); } catch { /* ignore */ }
}

/** Delete every notification of `uid` (clear inbox). */
export async function clearAllNotifications(uid: string): Promise<void> {
  const db = getDb();
  if (!db || !uid) return;
  try {
    const snap = await getDocs(collection(db, "users", uid, "notifications"));
    if (snap.empty) return;
    // Firestore batch max = 500 writes; chunk defensively at 400.
    const ids = snap.docs.map((d) => d.id);
    for (let i = 0; i < ids.length; i += 400) {
      const batch = writeBatch(db);
      for (const id of ids.slice(i, i + 400)) {
        batch.delete(doc(db, "users", uid, "notifications", id));
      }
      await batch.commit();
    }
  } catch { /* ignore */ }
}



/* ---------------- Favorites / Watchlist (per-user) ---------------- */

function userColl(uid: string, sub: "favorites" | "watchlist" | "history") {
  const db = getDb();
  if (!db) return null;
  return collection(db, "users", uid, sub);
}

export function useFavoritesRemote(uid: string | null): FavoriteRef[] {
  const [items, setItems] = useState<FavoriteRef[]>([]);
  useEffect(() => {
    if (!uid) {
      setItems([]);
      return;
    }
    const c = userColl(uid, "favorites");
    if (!c) return;
    const unsub = onSnapshot(query(c, orderBy("addedAt", "desc"), limit(500)), (snap) => {
      const out: FavoriteRef[] = [];
      snap.forEach((s) => {
        const d = s.data() as DocumentData;
        out.push({ slug: s.id, kind: d.kind, addedAt: d.addedAt ?? 0 });
      });
      setItems(out);
    });
    return () => unsub();
  }, [uid]);
  return items;
}

/** Derive the CommentsSection threadId from a saved-item (slug, kind). */
function threadIdForItem(slug: string, kind: "movie" | "series"): string {
  return `${kind === "series" ? "series" : "movie"}-${slug}`;
}

/** Maintain a reverse index `threadWatchers/{threadId}` so `postComment`
 *  can fan out notifications to every user who favorited or watchlisted
 *  the item, without expensive collectionGroup queries. */
async function updateThreadWatcher(
  db: NonNullable<ReturnType<typeof getDb>>,
  threadId: string,
  uid: string,
  field: "favoriteUids" | "watchlistUids",
  on: boolean,
): Promise<void> {
  try {
    const ref = doc(db, "threadWatchers", threadId);
    await setDoc(
      ref,
      { [field]: on ? arrayUnion(uid) : arrayRemove(uid) },
      { merge: true },
    );
  } catch { /* index is best-effort */ }
}

export async function toggleFavoriteRemote(
  uid: string,
  slug: string,
  kind: "movie" | "series",
): Promise<boolean> {
  const db = getDb();
  if (!db) return false;
  const ref = doc(db, "users", uid, "favorites", slug);
  const snap = await getDoc(ref);
  const tid = threadIdForItem(slug, kind);
  if (snap.exists()) {
    await deleteDoc(ref);
    await updateThreadWatcher(db, tid, uid, "favoriteUids", false);
    return false;
  }
  await setDoc(ref, { kind, addedAt: Date.now() });
  await updateThreadWatcher(db, tid, uid, "favoriteUids", true);
  return true;
}

export function useWatchlistRemote(uid: string | null): WatchlistRef[] {
  const [items, setItems] = useState<WatchlistRef[]>([]);
  useEffect(() => {
    if (!uid) {
      setItems([]);
      return;
    }
    const c = userColl(uid, "watchlist");
    if (!c) return;
    const unsub = onSnapshot(query(c, orderBy("addedAt", "desc"), limit(500)), (snap) => {
      const out: WatchlistRef[] = [];
      snap.forEach((s) => {
        const d = s.data() as DocumentData;
        out.push({ slug: s.id, kind: d.kind, addedAt: d.addedAt ?? 0 });
      });
      setItems(out);
    });
    return () => unsub();
  }, [uid]);
  return items;
}

export async function toggleWatchlistRemote(
  uid: string,
  slug: string,
  kind: "movie" | "series",
): Promise<boolean> {
  const db = getDb();
  if (!db) return false;
  const ref = doc(db, "users", uid, "watchlist", slug);
  const snap = await getDoc(ref);
  const tid = threadIdForItem(slug, kind);
  if (snap.exists()) {
    await deleteDoc(ref);
    await updateThreadWatcher(db, tid, uid, "watchlistUids", false);
    return false;
  }
  await setDoc(ref, { kind, addedAt: Date.now() });
  await updateThreadWatcher(db, tid, uid, "watchlistUids", true);
  return true;
}

/* ---------------- Watch history / Continue-watching ---------------- */

export async function saveWatchProgress(
  uid: string,
  key: string,
  data: Omit<WatchHistoryRef, "updatedAt">,
): Promise<void> {
  const db = getDb();
  if (!db) return;
  await setDoc(doc(db, "users", uid, "history", key), { ...data, updatedAt: Date.now() });
}

export function useWatchHistoryRemote(uid: string | null): WatchHistoryRef[] {
  const [items, setItems] = useState<WatchHistoryRef[]>([]);
  useEffect(() => {
    if (!uid) {
      setItems([]);
      return;
    }
    const c = userColl(uid, "history");
    if (!c) return;
    const unsub = onSnapshot(query(c, orderBy("updatedAt", "desc"), limit(200)), (snap) => {
      const out: WatchHistoryRef[] = [];
      snap.forEach((s) => {
        const d = s.data() as DocumentData;
        out.push({
          slug: d.slug,
          kind: d.kind,
          season: d.season,
          episode: d.episode,
          position: d.position ?? 0,
          duration: d.duration ?? 0,
          percent: d.percent ?? 0,
          updatedAt: d.updatedAt ?? 0,
        });
      });
      setItems(out);
    });
    return () => unsub();
  }, [uid]);
  return items;
}

/* ---------------- View counters (Firestore distributed) ---------------- */

/** Increment `/counters/{slug}/shards/{N}` by 1 via distributed shards. */
export function bumpViews(slug: string): void {
  if (!slug) return;
  // De-duplicate rapid double-mounts (StrictMode/dev) or refreshes within
  // the same tab.
  try {
    const k = `__rawan_view_${slug}`;
    const last = Number(sessionStorage.getItem(k) || 0);
    if (Date.now() - last < 30_000) return;
    sessionStorage.setItem(k, String(Date.now()));
  } catch {
    /* ignore */
  }

  const db = getDb();
  if (!db) {
    console.warn("[views] Firestore unavailable — counter not incremented for", slug);
    return;
  }
  bumpCounter(db, "counters", slug, VIEW_SHARDS);
}

/** Subscribe to the sum of all `/counters/{slug}/shards/{N}`. */
export function useViews(slug: string | undefined | null): number {
  return useCounter("counters", slug);
}

/* ---------------- Comment counters (Firestore distributed) ---------------- */

/** Subscribe to the sum of all `/commentCounters/{thread}/shards/{N}`. */
export function useCommentsCount(threadId: string | undefined | null): number {
  return useCounter("commentCounters", threadId);
}

/* ---------------- Admin: list/manage users & comments ---------------- */

export async function adminListUsers() {
  const db = getDb();
  if (!db) return [];
  const snap = await getDocs(query(collection(db, "users"), limit(500)));
  return snap.docs.map((s) => ({ id: s.id, ...(s.data() as DocumentData) }));
}

export async function adminSetUserBanned(uid: string, banned: boolean): Promise<void> {
  const db = getDb();
  if (!db) return;
  await updateDoc(doc(db, "users", uid), { banned });
}

export async function adminDeleteUserDoc(uid: string): Promise<void> {
  const db = getDb();
  if (!db) return;
  await deleteDoc(doc(db, "users", uid));
}

export async function adminListAllComments() {
  const db = getDb();
  if (!db) return [];
  const snap = await getDocs(
    query(collection(db, "comments"), orderBy("createdAt", "desc"), limit(500)),
  );
  return snap.docs.map((s) => ({ id: s.id, ...(s.data() as DocumentData) }));
}

/* ---------------- One-time batch helpers (optional) ---------------- */

/**
 * Migrate a single RTDB value into a Firestore distributed counter.
 * Call this once per slug from an admin script or browser console:
 *
 *   import { migrateRtdbViewToFirestore } from "./firebase-data";
 *   migrateRtdbViewToFirestore("my-movie", 1234);
 */
export async function migrateRtdbViewToFirestore(slug: string, total: number): Promise<void> {
  const db = getDb();
  if (!db || total <= 0) return;
  const batch = writeBatch(db);
  const perShard = Math.floor(total / VIEW_SHARDS);
  const remainder = total % VIEW_SHARDS;
  for (let i = 0; i < VIEW_SHARDS; i++) {
    const count = perShard + (i < remainder ? 1 : 0);
    if (count > 0) {
      const shardRef = doc(db, "counters", slug, "shards", String(i));
      batch.set(shardRef, { count });
    }
  }
  await batch.commit();
}

/**
 * Migrate a single RTDB comments count into Firestore distributed counter.
 */
export async function migrateRtdbCommentsCountToFirestore(threadId: string, total: number): Promise<void> {
  const db = getDb();
  if (!db || total <= 0) return;
  const batch = writeBatch(db);
  const perShard = Math.floor(total / COMMENT_SHARDS);
  const remainder = total % COMMENT_SHARDS;
  for (let i = 0; i < COMMENT_SHARDS; i++) {
    const count = perShard + (i < remainder ? 1 : 0);
    if (count > 0) {
      const shardRef = doc(db, "commentCounters", threadId, "shards", String(i));
      batch.set(shardRef, { count });
    }
  }
  await batch.commit();
}
